/**
 * Shared adapter implementation. Application code should import from
 * user-state-adapter.ts, not from this file.
 */

import type { LicenseModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import type { User } from '../user-state';

export type UserSnapshot = Readonly<{
  /** Single readable access to the current user. Prefer this over getActiveUserId/getName. */
  getActiveUser: () => User | null;
  licenses: readonly LicenseModel[];
  isLoaded: boolean;
  isOrganizationAdmin: boolean;
  isCompanyAdmin: boolean;
  getProjects: () => User['projects'] | undefined;
  getCompanyUsers: () => User['companyusers'] | undefined;
  hasValidDesignToolSeat: () => boolean;
  hasDesignToolAccessForCurrentProject: () => boolean;
  isActiveRoleProjectUser: () => boolean;
}>;

type Listener = () => void;

const emptyGetActiveUser = (): User | null => null;

const EMPTY_SNAPSHOT: UserSnapshot = Object.freeze({
  getActiveUser: emptyGetActiveUser,
  licenses: [],
  isLoaded: false,
  isOrganizationAdmin: false,
  isCompanyAdmin: false,
  getProjects: () => undefined,
  getCompanyUsers: () => undefined,
  hasValidDesignToolSeat: () => false,
  hasDesignToolAccessForCurrentProject: () => false,
  isActiveRoleProjectUser: () => false,
});

interface UserStateAdapterPublic {
  getSnapshot(): UserSnapshot;
  subscribe(listener: Listener): () => void;
}

class UserStateAdapterImpl implements UserStateAdapterPublic {
  private snapshot: UserSnapshot = EMPTY_SNAPSHOT;
  private listeners = new Set<Listener>();

  getSnapshot(): UserSnapshot {
    return this.snapshot;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Internal: only UserStateAdapterBridge may call this. */
  private _setSnapshot(next: UserSnapshot) {
    this.snapshot = Object.freeze(next);
    this.listeners.forEach((l) => l());
  }
}

export const userStateAdapter = new UserStateAdapterImpl();
